/* eslint-disable react/no-unused-prop-types */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Divider,
  Form,
  Grid,
  Icon,
  Label,
} from 'semantic-ui-react';
import { required, stringLength } from '@mhub/web-validations';
import AppForm from '../../../components/AppForm';
import BusinessUnitDropdown from '../../../components/Sortable/SortablePackagePricing/BusinessUnitDropdown';
import SchedulePicker from '../../../components/Sortable/SortablePackagePricing/SchedulePicker';
import SortablePackagePricingItem from '../../../components/Sortable/SortablePackagePricing/SortablePackagePricingItem';
import { PROJECT_PACKAGE_PRICING_DEFAULT } from '../../../structs/package_pricing';
import {
  DEPOSIT_RATE_PERCENT_MAX,
  depositPercentToRate,
  depositRateToPercent,
  isDepositRatePercentValid,
} from '../../../utils/vietnamPricing';

export default class PackagePricingEdit extends Component {
  static propTypes = {
    data: PropTypes.shape(),
    // CPI-977: the deposit rate is a Vietnam-only package setting, passed down from the container.
    isVietnam: PropTypes.bool,
    onBulkManageUnits: PropTypes.func,
    onBulkUpdateUnits: PropTypes.func,
    onChange: PropTypes.func,
    onDelete: PropTypes.func,
    onDeleteItem: PropTypes.func,
    onEditingItems: PropTypes.func,
    onSortItems: PropTypes.func,
    refForm: PropTypes.func,
    refValidation: PropTypes.func,
  }
  static defaultProps = {
    data: Object.assign({}, PROJECT_PACKAGE_PRICING_DEFAULT),
    isVietnam: false,
  }
  // CPI-977: state holds what the agent typed; converting on every keystroke would eat the decimal.
  constructor(props) {
    super(props);
    this.state = {
      depositRatePercent: depositRateToPercent(props.data && props.data.depositRate),
    };
  }
  // An invalid or empty box writes null, not 0 - book-api reads NULL as "no rate on this package".
  handleDepositRateChange = (e, d) => {
    const { data } = this.props;
    data.depositRate = depositPercentToRate(d.value);
    this.setState({ depositRatePercent: d.value });
    if (this.props.onChange) this.props.onChange(data);
  }
  handleDelete = (e) => {
    e.preventDefault();
    const { data } = this.props;
    if (this.props.onDelete) this.props.onDelete(data);
  }
  handleSave = (e) => {
    if (e) e.preventDefault();
    const { data } = this.props;
    if (this.props.onChange) this.props.onChange(data);
  }
  handleInputChange = (e, d) => {
    const { data } = this.props;
    data[d.name] = d.value;
    if (this.props.onChange) this.props.onChange(data);
  }
  handleCheckboxChange = (e, d) => {
    const { data } = this.props;
    data[d.name] = !data[d.name];
    if (this.props.onChange) this.props.onChange(data);
  }
  handleScheduleConfirm = (value) => {
    const { data } = this.props;
    data.scheduleStartAt = value.scheduleStartAt;
    data.scheduleEndAt = value.scheduleEndAt;
    if (this.props.onChange) this.props.onChange(data);
  }
  handleBUAccessChange = (value) => {
    const { data } = this.props;
    data.businessUnitAccesses = value;
    if (this.props.onChange) this.props.onChange(data);
  }
  handleItemChange = (value) => {
    const { data } = this.props;
    // TODO: change to packagePricingCalculations
    data.packagePricingItems = value;
    if (this.props.onChange) this.props.onChange(data);
  }
  handleDeleteItem = (id, callback) => {
    if (this.props.onDeleteItem) this.props.onDeleteItem(id, callback);
  }
  handleEditingItem = (ids, data) => {
    if (this.props.onEditingItems) this.props.onEditingItems(ids, data);
  }
  handleSortItems = (isSorting) => {
    if (this.props.onSortItems) this.props.onSortItems(isSorting);
  }
  handleBulkMangeUnits = (add = true) => {
    const { data } = this.props;
    if (this.props.onBulkManageUnits) this.props.onBulkManageUnits(add, data.id);
  }
  handleBulkUpdateUnits = () => {
    const { data } = this.props;
    if (this.props.onBulkUpdateUnits) this.props.onBulkUpdateUnits(data.id);
  }
  renderDepositRate = () => {
    if (!this.props.isVietnam) {
      return null;
    }
    const { depositRatePercent } = this.state;
    const isValid = isDepositRatePercentValid(depositRatePercent);
    return (
      <Grid.Row className="row_deposit_rate">
        <Grid.Column mobile={16} tablet={6} computer={6}>
          <Form.Input
            label="Deposit rate (%)"
            max={DEPOSIT_RATE_PERCENT_MAX}
            min={0}
            name="depositRate"
            onChange={this.handleDepositRateChange}
            placeholder="e.g. 10"
            step="0.01"
            type="number"
            value={depositRatePercent}
          />
          <p className="text-subtitle" style={isValid ? null : { color: 'red' }}>
            {isValid
              ? 'Percentage of the nett price collected as the deposit. Leave empty to collect the project\u2019s fixed reservation amount instead.'
              : `Enter a percentage between 0 and ${DEPOSIT_RATE_PERCENT_MAX}. Anything else is stored as no rate, and the project\u2019s fixed reservation amount applies.`}
          </p>
        </Grid.Column>
      </Grid.Row>
    );
  }
  render() {
    const { data } = this.props;
    return (
      <div className="sortablehoc_item">
        <Grid>
          <Grid.Row>
            <Grid.Column>
              <AppForm
                as="div"
                onSubmit={this.handleSave}
                ref={this.props.refForm}
              >
                <Grid>
                  {/* Name & Description */}
                  <Grid.Row className="row_name_desc">
                    <Grid.Column mobile={16} tablet={6} computer={6}>
                      <AppForm.Input
                        label="Package Name"
                        name="name"
                        onChange={this.handleInputChange}
                        ref={this.props.refValidation}
                        type="text"
                        validateLabel="Package Name"
                        validation={[required, {
                          validate: stringLength,
                          option: {
                            max: 128,
                          },
                        }]}
                        value={data.name}
                      />
                    </Grid.Column>
                    <Grid.Column mobile={16} tablet={10} computer={10}>
                      <Label className="row_label" content="Package Description" />
                      <Form.TextArea
                        name="description"
                        onChange={this.handleInputChange}
                        type="text"
                        value={data.description}
                        rows={1}
                        style={{ whiteSpace: 'pre-wrap' }}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  {/* CPI-977: Deposit rate. Vietnam only -- the deposit there is a percentage
                      of the package's nett price (Standard 10%, Fast-Track 50%, Bank Loan 10%),
                      while Malaysia and Indonesia keep collecting the project's flat reservation
                      sum and are never shown this field. */}
                  {this.renderDepositRate()}
                  {/* Schedule & BU Access */}
                  <Grid.Row className="row_schedule_bu_access">
                    <Grid.Column mobile={16} tablet={16} computer={10}>
                      <SchedulePicker
                        data={data}
                        onConfirm={this.handleScheduleConfirm}
                      />
                    </Grid.Column>
                    {/* BU access */}
                    <Grid.Column mobile={16} tablet={16} computer={6}>
                      <BusinessUnitDropdown
                        onChange={this.handleBUAccessChange}
                        value={data.businessUnitAccesses || []}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  {/* Checkboxes & Delete */}
                  <Grid.Row verticalAlign="top" className="row_enable_bumi_delete">
                    <Grid.Column mobile={16} tablet={5} computer={4}>
                      <Form.Group>
                        <Form.Checkbox
                          checked={data.isEnabled}
                          label="Enable Package"
                          name="isEnabled"
                          onChange={this.handleCheckboxChange}
                          ref={this.props.refValidation}
                          toggle
                        />
                      </Form.Group>
                    </Grid.Column>
                    <Grid.Column mobile={16} tablet={8} computer={10}>
                      <Form.Group>
                        <Form.Checkbox
                          checked={data.bumiPurchaserOnly}
                          label="Package for primary bumi purchaser only"
                          name="bumiPurchaserOnly"
                          onChange={this.handleCheckboxChange}
                          ref={this.props.refValidation}
                        />
                      </Form.Group>
                    </Grid.Column>
                    <Grid.Column mobile={16} tablet={3} computer={2} className="column_delete">
                      <Button
                        size="small"
                        basic
                        compact
                        icon
                        onClick={this.handleDelete}
                      >
                        <Icon name="trash" color="red" />
                      </Button>
                    </Grid.Column>
                  </Grid.Row>
                  {/* Formula */}
                  <Grid.Row className="row_formula">
                    <Grid.Column width={16}>
                      <Divider />
                      <SortablePackagePricingItem
                        // TODO: change to packagePricingCalculations
                        data={data.packagePricingItems || []}
                        onChange={this.handleItemChange}
                        onDeleteItem={this.handleDeleteItem}
                        onEditingItems={this.handleEditingItem}
                        onSortItems={this.handleSortItems}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Divider />
                  {/* Bulk Manage Unit Pricing Package */}
                  <Grid.Row textAlign="center" className="row_bulk_manage_unit_pp" centered>
                    <Grid.Column mobile={16} tablet={15} computer={12}>
                      <Grid>
                        <Grid.Row>
                          <Grid.Column mobile={16} tablet={6} computer={6} verticalAlign="middle">
                            <Button
                              size="small"
                              basic
                              compact
                              disabled={data.id === ''}
                              onClick={() => this.handleBulkUpdateUnits()}
                            >
                              <Icon name="refresh" />
                              Recalculate in all units
                            </Button>
                          </Grid.Column>
                          <Grid.Column mobile={16} tablet={4} computer={4} verticalAlign="middle">
                            <Button
                              size="small"
                              primary
                              basic
                              compact
                              disabled={data.id === ''}
                              onClick={() => this.handleBulkMangeUnits(true)}
                            >
                              <Icon name="checkmark" color="blue" />
                              Apply to all units
                            </Button>
                          </Grid.Column>
                          <Grid.Column mobile={16} tablet={6} computer={6} verticalAlign="middle">
                            <Button
                              size="small"
                              negative
                              basic
                              compact
                              disabled={data.id === ''}
                              onClick={() => this.handleBulkMangeUnits(false)}
                            >
                              <Icon name="x" color="red" />
                              Remove from all units
                            </Button>
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </AppForm>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Sortable/SortablePackagePricing/PackagePricingEdit.jsx